import { RadioGroup } from "@headlessui/react";
import { twMerge } from "tailwind-merge";

type TValue = string | number | boolean;
type TLabel = string | number;

type TRadioOption = {
  /**
   * Option value.
   */
  value: TValue;
  /**
   * Option label.
   */
  label: TLabel;
  /**
   * Disabled option flag.
   */
  disabled?: boolean;
  /**
   * Custom radio option render element.
   */
  renderElement?: React.ReactNode;
};

export interface IRadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "defaultValue"> {
  /**
   * Radio group options.
   */
  options: TRadioOption[];
  /**
   * Radio group name.
   */
  name: string;
  /**
   * Radio group error.
   */
  error?: string;
  /**
   * Option change callback.
   */
  onRadioOptionChange?: (value: TValue) => void;
  /**
   * Radio group classname.
   */
  className?: string;
  /**
   * Radio input classname.
   */
  radioClassName?: string;
  /**
   * Label classname.
   */
  labelClassName?: string;
  /**
   * Radio input default value.
   */
  defaultValue?: TValue;
}

//TODO: implementation needs fixing
export const Radio: React.FC<IRadioProps> = ({
  defaultValue,
  disabled,
  options,
  onRadioOptionChange,
  error,
  className,
  radioClassName,
  labelClassName,
  ...rest
}) => {
  return (
    <RadioGroup
      value={defaultValue}
      className={twMerge("flex flex-col gap-3", className)}
    >
      {options.map((option) => (
        <RadioGroup.Option
          value={option.value}
          key={option.label}
          onChange={() => onRadioOptionChange?.(option.value)}
        >
          {({ checked }) => (
            <div className="flex items-center gap-2">
              <input
                id={option.label.toString()}
                type="radio"
                defaultChecked={checked}
                disabled={disabled}
                {...rest}
                className={twMerge(
                  "peer relative size-5 cursor-pointer appearance-none rounded-full border border-green-dark text-green-light",
                  "checked:border-green-dark checked:bg-none",
                  "checked:hover:border-green-dark checked:focus:border-green-dark",
                  "checked:before:absolute checked:before:inset-1/2 checked:before:size-2 checked:before:-translate-x-1/2 checked:before:-translate-y-1/2 checked:before:rounded-full checked:before:bg-green-dark checked:before:bg-[length:8px_8px]",
                  "focus:border-green-dark focus:ring-base-black focus:ring-opacity-20 focus:ring-offset-0",
                  "disabled:cursor-default disabled:border-grey-dark disabled:bg-grey-light",
                  "disabled:checked:border-grey-dark disabled:checked:before:bg-grey-dark",
                  "disabled:hover:border-grey-dark disabled:hover:bg-grey-light",
                  radioClassName,
                )}
              />
              <RadioGroup.Label
                htmlFor={option.label.toString()}
                className={twMerge(
                  "cursor-pointer text-sm font-medium text-grey-dark",
                  disabled && "cursor-default",
                  labelClassName,
                )}
              >
                {option.renderElement ?? <p>{option.label}</p>}
              </RadioGroup.Label>
            </div>
          )}
        </RadioGroup.Option>
      ))}
      {error && (
        <span className="break-all text-sm text-red-dark">{error}</span>
      )}
    </RadioGroup>
  );
};
